import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import FloatingHeader from '../components/layout/FloatingHeader';
import PageMeta from '../components/PageMeta';
import Footer from '../components/Footer';
import Lichtszene from '../components/licht/Lichtszene';
import { SEITEN_BLENDE } from '../components/animations/seitenBlende';
import '../components/recht/recht.css';
// Formular, Karten, Knöpfe und Druckansicht teilt die Seite mit der Widerrufsfunktion (Klassen vw-*)
import './VertragWiderrufen.css';
import './VertraegeKuendigen.css';

// Kündigungsbutton nach § 312k BGB für das All-in-One-Abo (Pflicht seit 01.07.2022).
// Einstieg ist der Knopf „Verträge hier kündigen“ im Footer (auf jeder Seite, ohne Login — eine
// Passwortabfrage davor ist unzulässig, KG 5 U 6/25). Er führt unmittelbar hierher, auf die
// Bestätigungsseite: Schritt 1 die Angaben aus § 312k Abs. 2 (Art der Kündigung, bei außerordentlicher
// mit Grund; Name; Vertrag; gewünschtes Ende, leer = frühestmöglich; E-Mail für die Bestätigung),
// Schritt 2 die Erklärung und der Knopf „Jetzt kündigen“; erst der schickt ab. Danach die Bestätigung
// mit Datum und Uhrzeit des Eingangs zum Speichern/Drucken; dieselben Angaben plus das Vertragsende
// kommen per E-Mail (backend/server.py, POST /api/cancellation).
// Auf dieser Seite steht bewusst NICHTS außer der Kündigung: keine Angebote, kein Pausieren, kein
// Support-Hinweis, kein „Bist du sicher?“ — der Inhalt ist abschließend geregelt (BGH I ZR 200/25).

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Gleiche Bezeichnungen wie CANCELLATION_TYPES in server.py (Schlüssel = Produkt-IDs der Abos)
const TYPEN = {
  'abo-monat': ['All-in-One-Abo (monatlich)', 'All-in-One subscription (monthly)'],
  'abo-jahr': ['All-in-One-Abo (jährlich)', 'All-in-One subscription (yearly)'],
};

const zusammen = s => s.split(/\s+/).filter(Boolean).join(' ');

const datumText = (iso, en) => {
  if (!iso) return '';
  const [j, m, t] = iso.split('-');
  return en || !t ? iso : `${t}.${m}.${j}`;
};

// Wortlaut der Kündigung — muss zu _kuendigung_erklaerung() in server.py passen, denn genau dieser
// Text steht später in der Eingangsbestätigung.
function erklaerung({ art, grund, name, email, typ, angaben, datum }, en) {
  const teile = [];
  if (TYPEN[typ]) teile.push(TYPEN[typ][en ? 1 : 0]);
  const a = zusammen(angaben).replace(/[ .;]+$/, '');
  if (a) teile.push(a);
  const vertrag = teile.join('; ');
  const wer = `${zusammen(name)} (${email.trim().toLowerCase()})`;
  const d = datumText(datum, en);
  if (art === 'ausserordentlich') {
    let g = zusammen(grund).replace(/[ ;]+$/, '');
    if (g && !/[.!?]$/.test(g)) g += '.';
    return en
      ? `I, ${wer}, hereby terminate the following contract for good cause ${d ? `with effect from ${d}` : 'with immediate effect'}: ${vertrag}. Reason: ${g}`
      : `Hiermit kündige ich, ${wer}, folgenden Vertrag außerordentlich aus wichtigem Grund ${d ? `zum ${d}` : 'mit sofortiger Wirkung'}: ${vertrag}. Grund: ${g}`;
  }
  return en
    ? `I, ${wer}, hereby give ordinary notice of termination of the following contract ${d ? `with effect from ${d}` : 'at the earliest possible date'}: ${vertrag}.`
    : `Hiermit kündige ich, ${wer}, folgenden Vertrag ordentlich ${d ? `zum ${d}` : 'zum nächstmöglichen Zeitpunkt'}: ${vertrag}.`;
}

const MAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// „E-Mail“ nicht am Bindestrich umbrechen (sonst steht „E-“ am Zeilenende): unsichtbarer Wortverbinder
const festeMail = t => (typeof t === 'string' ? t.replace(/E-Mail/g, 'E-\u2060Mail') : t);

const heute = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function pruefe(w, en) {
  const f = {};
  if (w.art === 'ausserordentlich' && !w.grund.replace(/[\s.;]/g, '')) f.grund = en
    ? 'Please state the reason for the extraordinary termination.'
    : 'Bitte gib den Grund für die außerordentliche Kündigung an.';
  if (!zusammen(w.name)) f.name = en ? 'Please enter your name.' : 'Bitte gib deinen Namen an.';
  if (!MAIL_OK.test(w.email.trim())) f.email = en ? 'Please enter a valid email address so we can send you the confirmation.' : 'Bitte gib eine gültige E-Mail-Adresse an, damit wir dir die Bestätigung schicken können.';
  if (!w.typ && !w.angaben.replace(/[\s.;]/g, '')) f.vertrag = en
    ? 'Please tell us which contract you want to cancel: choose one above or describe it here.'
    : 'Bitte gib an, welchen Vertrag du kündigen möchtest: oben auswählen oder hier beschreiben.';
  if (w.datum && w.datum < heute()) f.datum = en ? 'Please choose today or a later date.' : 'Bitte wähle heute oder ein späteres Datum.';
  return f;
}

export default function VertraegeKuendigen() {
  const location = useLocation();
  const en = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const T = (de, e) => (en ? e : de);

  const [werte, setWerte] = useState({ art: 'ordentlich', grund: '', name: '', email: '', typ: '', angaben: '', datum: '', website: '' });
  const [fehler, setFehler] = useState({});
  const [versucht, setVersucht] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState(null); // 'server' | 'zuviel' | { text }
  const [ergebnis, setErgebnis] = useState(null);
  const karte = useRef(null);
  const meldungRef = useRef(null);

  // Wer angemeldet ist, bekommt Name und E-Mail vorausgefüllt (nur Komfort, änderbar; die Seite
  // funktioniert genauso ohne Konto). Ohne Anmeldung oder ohne Server passiert hier einfach nichts.
  useEffect(() => {
    let token = null;
    try { token = localStorage.getItem('aether_token'); } catch { token = null; }
    if (!token) return undefined;
    let aus = false;
    fetch(`${API_URL}/api/aether/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(ich => {
        if (aus || !ich) return;
        setWerte(w => ({ ...w, name: w.name || ich.name || '', email: w.email || ich.email || '' }));
      })
      .catch(() => {});
    return () => { aus = true; };
  }, []);

  const setze = (feld, wert) => {
    const neu = { ...werte, [feld]: wert };
    setWerte(neu);
    if (versucht) setFehler(pruefe(neu, en));
  };

  const vollstaendig = Object.keys(pruefe(werte, en)).length === 0;
  const text = useMemo(() => erklaerung(werte, en), [werte, en]);
  const ausser = werte.art === 'ausserordentlich';

  useEffect(() => { if (meldung && meldungRef.current) meldungRef.current.focus(); }, [meldung]);
  useEffect(() => {
    if (ergebnis && karte.current) {
      window.scrollTo({ top: karte.current.getBoundingClientRect().top + window.scrollY - 110, behavior: 'smooth' });
    }
  }, [ergebnis]);

  const absenden = async e => {
    e.preventDefault();
    if (sendet) return;
    setVersucht(true);
    const f = pruefe(werte, en);
    setFehler(f);
    setMeldung(null);
    if (Object.keys(f).length) {
      const erstes = { grund: 'vk-grund', name: 'vk-name', email: 'vk-email', vertrag: 'vk-angaben', datum: 'vk-datum' }[Object.keys(f)[0]];
      const el = document.getElementById(erstes);
      if (el) el.focus();
      return;
    }
    setSendet(true);
    const abbruch = new AbortController();
    const uhr = setTimeout(() => abbruch.abort(), 20000);
    try {
      const res = await fetch(`${API_URL}/api/cancellation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abbruch.signal,
        body: JSON.stringify({
          kind: werte.art,
          reason: ausser ? werte.grund.trim() : '',
          name: zusammen(werte.name),
          email: werte.email.trim(),
          contract_type: werte.typ,
          contract: werte.angaben.trim(),
          end_date: werte.datum,
          lang: en ? 'en' : 'de',
          website: werte.website,
        }),
      });
      let daten = null;
      try { daten = await res.json(); } catch { daten = null; }
      if (res.ok && daten && daten.ok) {
        setErgebnis({ ...daten, email: werte.email.trim() });
      } else if (res.status === 429) {
        setMeldung('zuviel');
      } else if (res.status === 400 && daten && typeof daten.detail === 'string') {
        setMeldung({ text: daten.detail });
      } else if (res.status === 422) {
        setMeldung({ text: T('Bitte prüf deine Angaben (ein Feld ist zu lang oder leer).', 'Please check your details (a field is too long or empty).') });
      } else {
        setMeldung('server');
      }
    } catch {
      setMeldung('server');
    } finally {
      clearTimeout(uhr);
      setSendet(false);
    }
  };

  const fehlerText = id => fehler[id] && <p className="vw-feldfehler" id={`vk-${id}-fehler`}>{fehler[id]}</p>;

  return (
    <motion.div {...SEITEN_BLENDE}>
      <PageMeta
        title={T('Verträge kündigen | DAVIDMEDIA', 'Cancel contracts | DAVIDMEDIA')}
        description={T('Kündige dein All-in-One-Abo bei David Media online in zwei Schritten, ohne Anmeldung. Die Eingangsbestätigung kommt sofort per E-Mail.',
          'Cancel your All-in-One subscription with David Media online in two steps, without signing in. The confirmation of receipt arrives by email right away.')}
        url={`https://www.davidmedia.de${en ? '/en/cancel-contract' : '/vertraege-kuendigen'}`}
        noindex
      />
      <FloatingHeader />
      <div className="rechtsseite vw-seite vk-seite" lang={en ? 'en' : 'de'}>
        <Lichtszene />
        <header className="recht-kopf">
          <span className="kicker">{T('Kündigung', 'Cancellation')}</span>
          <h1>{T('Verträge kündigen', 'Cancel contracts')}<span className="grau">{T('Online, in zwei Schritten. Ohne Anmeldung.', 'Online, in two steps. No sign-in needed.')}</span></h1>
        </header>

        <div className="vw-raster">
          <div className={'vw-karte' + (ergebnis ? ' vw-druck' : '')} ref={karte}>
            {!ergebnis ? (
              <form onSubmit={absenden} noValidate>
                <ol className="vw-schritte" aria-label={T('Ablauf', 'Steps')}>
                  <li className={vollstaendig ? 'fertig' : 'an'}><b>01</b>{T('Angaben', 'Details')}</li>
                  <li className={vollstaendig ? 'an' : ''}><b>02</b>{T('Jetzt kündigen', 'Cancel now')}</li>
                </ol>

                <fieldset className="vw-abschnitt">
                  {/* Kicker IN der legend: eine legend steht sonst immer ganz oben im fieldset */}
                  <legend><span className="kicker vw-nr">{T('Schritt 1', 'Step 1')}</span>{T('Deine Angaben', 'Your details')}</legend>
                  <p className="vw-unter">{festeMail(T('Art der Kündigung, dein Name, der Vertrag und eine E-Mail-Adresse für die Bestätigung. Einen Grund brauchst du nur bei einer außerordentlichen Kündigung.',
                    'Type of termination, your name, the contract and an email address for the confirmation. A reason is only needed for an extraordinary termination.'))}</p>

                  <div className="vw-felder">
                    <div className="vw-feld breit" role="radiogroup" aria-labelledby="vk-art-titel">
                      <span className="vw-label" id="vk-art-titel">{T('Art der Kündigung', 'Type of termination')}</span>
                      <div className="vw-wahl">
                        {[
                          ['ordentlich', T('Ordentliche Kündigung', 'Ordinary termination'), T('Zum nächstmöglichen Zeitpunkt, also zum Ende des laufenden Abrechnungszeitraums', 'At the earliest possible date, i.e. at the end of the current billing period')],
                          ['ausserordentlich', T('Außerordentliche Kündigung', 'Extraordinary termination'), T('Aus wichtigem Grund, mit sofortiger Wirkung', 'For good cause, with immediate effect')],
                        ].map(([wert, titel, klein]) => (
                          <label key={wert}>
                            <input type="radio" name="art" value={wert} checked={werte.art === wert}
                              onChange={() => setze('art', wert)} data-testid={`kuendigung-art-${wert}`} />
                            <span className="vw-punkt" aria-hidden="true" />
                            <b>{titel}</b>
                            <span className="vw-klein">{klein}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {ausser && (
                      <div className="vw-feld breit vk-grund">
                        <label htmlFor="vk-grund">{T('Kündigungsgrund', 'Reason for termination')}</label>
                        <textarea id="vk-grund" name="grund" rows={3} maxLength={2000}
                          value={werte.grund} onChange={e => setze('grund', e.target.value)}
                          placeholder={T('Warum kündigst du außerordentlich?', 'Why are you terminating for good cause?')}
                          aria-invalid={!!fehler.grund} aria-describedby={fehler.grund ? 'vk-grund-fehler' : undefined} />
                        {fehlerText('grund')}
                      </div>
                    )}

                    <div className="vw-feld">
                      <label htmlFor="vk-name">{T('Name', 'Name')}</label>
                      <input id="vk-name" name="name" type="text" autoComplete="name" maxLength={200}
                        value={werte.name} onChange={e => setze('name', e.target.value)}
                        placeholder={T('Vor- und Nachname', 'First and last name')}
                        aria-invalid={!!fehler.name} aria-describedby={fehler.name ? 'vk-name-fehler' : undefined} />
                      {fehlerText('name')}
                    </div>
                    <div className="vw-feld">
                      <label htmlFor="vk-email">{T('E-Mail für die Bestätigung', 'Email for the confirmation')}</label>
                      <input id="vk-email" name="email" type="email" autoComplete="email" inputMode="email" maxLength={200}
                        value={werte.email} onChange={e => setze('email', e.target.value)}
                        placeholder={T('name@beispiel.de', 'name@example.com')}
                        aria-invalid={!!fehler.email} aria-describedby={fehler.email ? 'vk-email-fehler' : 'vk-email-hilfe'} />
                      {fehler.email ? fehlerText('email') : <p className="vw-hilfe" id="vk-email-hilfe">{T('Am besten die Adresse, mit der du das Abo abgeschlossen hast.', 'Ideally the address you subscribed with.')}</p>}
                    </div>

                    <div className="vw-feld breit" role="radiogroup" aria-labelledby="vk-typ-titel">
                      <span className="vw-label" id="vk-typ-titel">{T('Welchen Vertrag möchtest du kündigen?', 'Which contract do you want to cancel?')}</span>
                      <div className="vw-wahl" data-fehler={fehler.vertrag ? 'ja' : undefined}>
                        {[
                          ['abo-monat', T('All-in-One, monatlich', 'All-in-One, monthly'), T('Abrechnung jeden Monat', 'Billed every month')],
                          ['abo-jahr', T('All-in-One, jährlich', 'All-in-One, yearly'), T('Abrechnung einmal im Jahr', 'Billed once a year')],
                        ].map(([wert, titel, klein]) => (
                          <label key={wert}>
                            <input type="radio" name="typ" value={wert} checked={werte.typ === wert}
                              onChange={() => setze('typ', wert)}
                              onClick={() => { if (werte.typ === wert) setze('typ', ''); }} />
                            <span className="vw-punkt" aria-hidden="true" />
                            <b>{titel}</b>
                            <span className="vw-klein">{klein}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="vw-feld breit">
                      <label htmlFor="vk-angaben">{T('Angaben zum Vertrag', 'Contract details')}<small className="vw-tag">{werte.typ ? T('Optional', 'Optional') : ''}</small></label>
                      <textarea id="vk-angaben" name="angaben" rows={3} maxLength={1000}
                        value={werte.angaben} onChange={e => setze('angaben', e.target.value)}
                        placeholder={T('z. B. Abo-Kennung, Rechnungs- oder Bestellnummer, Lizenzschlüssel', 'e.g. subscription ID, invoice or order number, license key')}
                        aria-invalid={!!fehler.vertrag} aria-describedby={fehler.vertrag ? 'vk-vertrag-fehler' : 'vk-angaben-hilfe'} />
                      {fehler.vertrag ? fehlerText('vertrag') : (
                        <p className="vw-hilfe" id="vk-angaben-hilfe">{T('Hilft uns beim Zuordnen, ist aber kein Muss. Weißt du nicht, ob monatlich oder jährlich, lass die Auswahl oben leer und beschreib den Vertrag hier.',
                          'Helps us find it, but isn’t required. If you are not sure whether it is monthly or yearly, leave the choice above empty and describe the contract here.')}</p>
                      )}
                    </div>

                    <div className="vw-feld">
                      <label htmlFor="vk-datum">{T('Beenden zum', 'End on')}<small className="vw-tag">{T('Optional', 'Optional')}</small></label>
                      <input id="vk-datum" name="datum" type="date" min={heute()}
                        value={werte.datum} onChange={e => setze('datum', e.target.value)}
                        aria-invalid={!!fehler.datum} aria-describedby={fehler.datum ? 'vk-datum-fehler' : 'vk-datum-hilfe'} />
                      {fehler.datum ? fehlerText('datum') : (
                        <p className="vw-hilfe" id="vk-datum-hilfe">{ausser
                          ? T('Leer lassen = mit sofortiger Wirkung.', 'Leave empty = with immediate effect.')
                          : T('Leer lassen = zum nächstmöglichen Zeitpunkt.', 'Leave empty = at the earliest possible date.')}</p>
                      )}
                    </div>
                  </div>

                  {/* Honigtopf: Menschen sehen und erreichen das Feld nicht, Bots füllen es aus */}
                  <div className="vw-hp" aria-hidden="true">
                    <label htmlFor="vk-website">Website</label>
                    <input id="vk-website" name="website" type="text" tabIndex={-1} autoComplete="off"
                      value={werte.website} onChange={e => setze('website', e.target.value)} />
                  </div>
                </fieldset>

                <section className="vw-abschnitt" aria-labelledby="vk-schritt2">
                  <span className="kicker vw-nr">{T('Schritt 2', 'Step 2')}</span>
                  <h2 id="vk-schritt2">{T('Jetzt kündigen', 'Cancel now')}</h2>
                  <p className="vw-unter">{T('Das schickst du ab:', 'This is what you send:')}</p>
                  <div className={'vw-erklaerung' + (vollstaendig ? '' : ' leer')} aria-live="polite">
                    {vollstaendig ? (
                      <><span className="kicker">{T('Deine Kündigung', 'Your cancellation')}</span>{text}</>
                    ) : T('Sobald alle nötigen Angaben gemacht sind, steht hier deine Kündigung.',
                      'As soon as all required details are filled in, your cancellation appears here.')}
                  </div>
                  <div className="vw-absenden">
                    <button type="submit" className="btn primaer" disabled={sendet} data-testid="jetzt-kuendigen">
                      {sendet && <span className="vw-dreher" aria-hidden="true" />}
                      {T('Jetzt kündigen', 'Cancel now')}
                    </button>
                    <p>{festeMail(T('Mit dem Klick kündigst du verbindlich. Die Eingangsbestätigung kommt sofort per E-Mail.',
                      'Clicking submits your cancellation. The confirmation of receipt arrives by email right away.'))}</p>
                  </div>

                  {meldung && (
                    <div className="vw-meldung" role="alert" tabIndex={-1} ref={meldungRef}>
                      {meldung === 'server' && (en ? (
                        <><b>The server isn’t responding right now.</b> Your cancellation has <b>not</b> been sent yet. Please try again in a few minutes. You can also cancel by email to <a href="mailto:contact@davidmedia.de">contact@davidmedia.de</a>.</>
                      ) : (
                        <><b>Der Server antwortet gerade nicht.</b> Deine Kündigung wurde <b>noch nicht</b> übermittelt. Bitte versuch es in ein paar Minuten noch einmal. Du kannst auch per E-Mail an <a href="mailto:contact@davidmedia.de">contact@davidmedia.de</a> kündigen.</>
                      ))}
                      {meldung === 'zuviel' && T('Zu viele Versuche in kurzer Zeit. Bitte warte ein paar Minuten und versuch es dann noch einmal.',
                        'Too many attempts in a short time. Please wait a few minutes and try again.')}
                      {typeof meldung === 'object' && meldung.text}
                    </div>
                  )}
                </section>
              </form>
            ) : (
              <div className="vw-fertig" role="status">
                {/* Nur im Ausdruck/PDF: wovon das Blatt stammt */}
                <p className="vk-nurdruck">David Media · davidmedia.de{en ? '/en/cancel-contract' : '/vertraege-kuendigen'}</p>
                <div className="vw-haken" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg></div>
                <h2>{T('Deine Kündigung ist eingegangen.', 'Your cancellation has been received.')}</h2>
                <p className="vw-unter">
                  {ergebnis.confirmation_sent
                    ? festeMail(T(`Die Eingangsbestätigung mit dem Vertragsende ist per E-Mail an ${ergebnis.email} unterwegs.`, `The confirmation of receipt, including when your contract ends, is on its way to ${ergebnis.email}.`))
                    : T('Wir haben deine Kündigung gespeichert.', 'We have saved your cancellation.')}
                </p>
                <dl className="vw-daten">
                  <div><dt>{T('Eingegangen am', 'Received on')}</dt><dd>{ergebnis.received_at_text}</dd></div>
                  <div><dt>{T('Referenz', 'Reference')}</dt><dd className="mono">{ergebnis.reference}</dd></div>
                  <div><dt>{T('Vertragsende', 'Contract ends')}</dt><dd>{festeMail(ergebnis.end_text)}</dd></div>
                  <div><dt>{T('Bestätigung an', 'Confirmation to')}</dt><dd>{ergebnis.email}</dd></div>
                </dl>
                <div className="vw-erklaerung"><span className="kicker">{T('Deine Kündigung', 'Your cancellation')}</span>{ergebnis.declaration}</div>
                {!ergebnis.confirmation_sent && (
                  <p className="vw-hinweis warnung">{T('Die Bestätigungs-Mail konnte gerade nicht verschickt werden. Deine Kündigung ist trotzdem eingegangen. Sichere diese Seite als Nachweis (Drucken › Als PDF sichern); wir schicken dir die Bestätigung nach.',
                    'The confirmation email could not be sent just now. Your cancellation has still been received. Save this page as proof (Print › Save as PDF); we will send you the confirmation later.')}</p>
                )}
                <p className="vw-hinweis">{T('Abgegeben über die Schaltfläche „Jetzt kündigen“ auf davidmedia.de. Sichere diese Seite als Nachweis mit Datum und Uhrzeit.',
                  'Submitted using the “Cancel now” button on davidmedia.de. Save this page as proof including date and time.')}</p>
                <div className="vw-knoepfe">
                  <button type="button" className="btn glas" onClick={() => window.print()}>{T('Drucken oder als PDF sichern', 'Print or save as PDF')}</button>
                  <Link className="btn glas" to={en ? '/en' : '/'}>{T('Zur Startseite', 'Back to home')}</Link>
                </div>
              </div>
            )}
          </div>

          {/* Nur der Ablauf und der Empfänger — keine Links zu Support, Konto oder Angeboten (BGH I ZR 200/25) */}
          <aside className="vw-seitenleiste" aria-label={T('So läuft es', 'How it works')}>
            <div className="vw-info">
              <span className="kicker">{T('So läuft es', 'How it works')}</span>
              <ol>
                <li><b>{T('Angaben machen', 'Enter your details')}</b><span>{festeMail(T('Art der Kündigung, Name, Vertrag und E-Mail.', 'Type of termination, name, contract and email.'))}</span></li>
                <li><b>{T('Jetzt kündigen', 'Cancel now')}</b><span>{T('Erst dieser Klick schickt die Kündigung ab.', 'Only this click submits the cancellation.')}</span></li>
                <li><b>{T('Eingangsbestätigung', 'Confirmation of receipt')}</b><span>{festeMail(T('Kommt sofort per E-Mail, mit deiner Kündigung, Datum und Uhrzeit des Eingangs und dem Vertragsende.', 'Arrives by email right away, with your cancellation, the date and time of receipt and when your contract ends.'))}</span></li>
              </ol>
            </div>
            <div className="vw-info">
              <span className="kicker">{T('Empfänger', 'Recipient')}</span>
              <address>David Ferfers · David Media<br />Bruchhöfe 21, 47829 Krefeld{en ? ', Germany' : ''}</address>
            </div>
          </aside>
        </div>
      </div>
      <div className="relative z-10"><Footer /></div>
    </motion.div>
  );
}
